import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { FilterBar } from '@/components/common/FilterBar';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { GroupedSwapsTable } from './GroupedSwapsTable';
import { swapsScannerApi } from '@/api/services/swapsScanner';
import { SwapsScannerPermissions as P } from './permissions';

const BROKERS_MIN = 1;
const BROKERS_MAX = 4;

function yesterday(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface SwapsFilters {
  referenceDate: string;
  monitorPercentage: number;
  brokers: string[];
  forceRefresh: boolean;
}

export function SwapsScannerPage() {
  const toast = useToast();
  const can = usePermission();

  const tabs: TabItem[] = [
    can(P.internalTab) && { key: 'internal', title: 'Internal Swap', permission: P.internalTab },
    can(P.externalTab) && { key: 'external', title: 'External Swap', permission: P.externalTab },
    can(P.brokersTab) && { key: 'brokers', title: 'Brokers Swap', permission: P.brokersTab },
  ].filter(Boolean) as TabItem[];

  const [active, setActive] = useState(tabs[0]?.key ?? 'internal');
  const [draft, setDraft] = useState<SwapsFilters>({ referenceDate: yesterday(), monitorPercentage: 0, brokers: [], forceRefresh: false });
  const [applied, setApplied] = useState<SwapsFilters>(draft);
  const [exportOpen, setExportOpen] = useState(false);
  // Fetch is an explicit refresh (forceRefresh:true), so re-clicking with identical
  // filters must still re-request. react-query keys by value, so bump this nonce on
  // every Fetch to defeat the cache hit. fetchPulse gives the button a brief spinner.
  const [fetchNonce, setFetchNonce] = useState(0);
  const [fetchPulse, setFetchPulse] = useState(false);

  // Legacy seeds the Monitor Percentage input from the tenant setting SwapMonitorPercentage (Index.cshtml.cs OnGet).
  const seededRef = useRef(false);
  const monitorPct = useQuery({
    queryKey: ['swaps-monitor-percentage'],
    queryFn: () => swapsScannerApi.getMonitorPercentage(),
    staleTime: Infinity,
  });
  useEffect(() => {
    if (seededRef.current || monitorPct.data == null) return;
    seededRef.current = true;
    setDraft((f) => ({ ...f, monitorPercentage: monitorPct.data }));
    setApplied((f) => ({ ...f, monitorPercentage: monitorPct.data }));
  }, [monitorPct.data]);

  const brokersOptions = useQuery({
    queryKey: ['swaps-brokers'],
    queryFn: () => swapsScannerApi.getPaginatedBrokers('', 0, 1000).then((r) => r.items.map((b) => ({ value: b.value, label: b.text }))),
    staleTime: 5 * 60_000,
  });

  const internal = useQuery({
    queryKey: ['swaps-internal', applied.referenceDate, applied.monitorPercentage, applied.forceRefresh, fetchNonce],
    queryFn: () => swapsScannerApi.getInternalSwap({ referenceDate: applied.referenceDate, monitorPercentage: applied.monitorPercentage, forceRefresh: applied.forceRefresh }),
    enabled: active === 'internal' && !!applied.referenceDate,
  });
  const external = useQuery({
    queryKey: ['swaps-external', applied.referenceDate, applied.monitorPercentage, applied.forceRefresh, fetchNonce],
    queryFn: () => swapsScannerApi.getSummaryTab({ referenceDate: applied.referenceDate, monitorPercentage: applied.monitorPercentage, forceRefresh: applied.forceRefresh }),
    enabled: active === 'external' && !!applied.referenceDate,
  });
  const raw = useQuery({
    queryKey: ['swaps-raw', applied.referenceDate, applied.monitorPercentage, applied.brokers.join('|'), applied.forceRefresh, fetchNonce],
    queryFn: () => swapsScannerApi.getRawTab({ referenceDate: applied.referenceDate, monitorPercentage: applied.monitorPercentage, brokers: applied.brokers.slice(0, BROKERS_MAX), endOfDay: true, forceRefresh: applied.forceRefresh }),
    enabled: active === 'brokers' && !!applied.referenceDate,
  });

  // Connector warning when reference date is today or later.
  const lastWarnRef = useRef<string>('');
  useEffect(() => {
    if (!applied.referenceDate || applied.referenceDate < todayStr()) return;
    if (lastWarnRef.current === applied.referenceDate) return;
    lastWarnRef.current = applied.referenceDate;
    swapsScannerApi.getConnectorWarning(applied.referenceDate).then((r) => { if (r?.message) toast.info(r.message); }).catch(() => {});
  }, [applied.referenceDate, toast]);

  const fetch = () => {
    if (!draft.referenceDate) { toast.info('Please select a reference date.'); return; }
    if (active === 'brokers') {
      if (draft.brokers.length < BROKERS_MIN) toast.info(`Please select between ${BROKERS_MIN} and ${BROKERS_MAX} brokers.`);
      else if (draft.brokers.length > BROKERS_MAX) toast.info(`Please select at most ${BROKERS_MAX} brokers.`);
    }
    setApplied({ ...draft, forceRefresh: true });
    setFetchNonce((n) => n + 1);
    setFetchPulse(true);
    window.setTimeout(() => setFetchPulse(false), 450);
  };

  async function requestExport(allBrokers: boolean) {
    setExportOpen(false);
    if (!draft.referenceDate) { toast.info('Please select a reference date.'); return; }
    const brokers = allBrokers ? [] : draft.brokers;
    if (!allBrokers && brokers.length === 0) { toast.info('Please select at least one broker, or choose "All Brokers".'); return; }
    try {
      const res = await swapsScannerApi.requestExcelExport({ referenceDate: draft.referenceDate, monitorPercentage: draft.monitorPercentage, allBrokers, brokers });
      if (res?.message) res.requested ? toast.success(res.message) : toast.info(res.message);
    } catch {
      toast.error('Failed to request the Excel export.');
    }
  }

  const rows = useMemo(() => {
    if (active === 'internal') return internal.data?.items ?? [];
    if (active === 'external') return external.data?.items ?? [];
    return raw.data?.items ?? [];
  }, [active, internal.data, external.data, raw.data]);

  const loading = active === 'internal' ? internal.isFetching : active === 'external' ? external.isFetching : raw.isFetching;

  return (
    <div className="swaps-scanner-page">
      <PageHeader title="Swaps Scanner" breadcrumb={['Home', 'Swaps Scanner']} />

      <FilterBar
        fields={[
          {
            node: (
              <div key="date">
                <label className="form-label">Reference Date</label>
                <DateRangePicker mode="single" value={draft.referenceDate} onChange={(v) => setDraft((f) => ({ ...f, referenceDate: v }))} />
              </div>
            ),
            pinned: true,
            width: 160,
          },
          <div key="mp">
            <label className="form-label">Monitor Percentage</label>
            <input className="form-control" inputMode="decimal" value={draft.monitorPercentage || ''} onChange={(e) => setDraft((f) => ({ ...f, monitorPercentage: Number(e.target.value) || 0 }))} />
          </div>,
          ...(active === 'brokers'
            ? [
                <div key="brokers">
                  <label className="form-label">Brokers (max {BROKERS_MAX})</label>
                  <Select2Multi placeholder="Select brokers" value={draft.brokers} options={brokersOptions.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, brokers: v }))} />
                </div>,
              ]
            : []),
        ]}
        actions={
          <>
            <button type="button" className="btn btn-primary btn-sm" onClick={fetch} disabled={loading || fetchPulse}>
              {loading || fetchPulse ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}Fetch
            </button>
            <div className="dropdown">
              <button type="button" className="btn btn-outline-primary btn-sm dropdown-toggle" onClick={() => setExportOpen((o) => !o)}>
                <i className="fa fa-download me-1" />Export to Excel
              </button>
              {exportOpen && (
                <ul className="dropdown-menu show" style={{ position: 'absolute' }}>
                  <li><button type="button" className="dropdown-item" onClick={() => requestExport(true)}>All Brokers</button></li>
                  <li><button type="button" className="dropdown-item" onClick={() => requestExport(false)}>Selected Brokers</button></li>
                </ul>
              )}
            </div>
          </>
        }
      />

      <div className="card custom-card border-0 shadow-none mb-2">
        <Tabs tabs={tabs} active={active} onChange={setActive} variant="underline" />
      </div>

      {active === 'internal' && <GroupedSwapsTable rows={rows} showIdentical excelFileName="Internal Swap" loading={loading} />}
      {active === 'external' && <GroupedSwapsTable rows={rows} showIdentical showStatus excelFileName="External Swap" loading={loading} />}
      {active === 'brokers' && <GroupedSwapsTable rows={rows} excelFileName="Brokers Swap" loading={loading} />}
    </div>
  );
}
